import { useRef, useEffect } from "react";
import { TempLogo } from "@/assets/temp-logo";
import { router } from "@/routes";
import gsap from "gsap";

interface LogoProps {
  subtitle?: string;
}

const LETTERS = "metawhale".split("");

export default function Logo({ subtitle }: LogoProps) {
  const iconRef = useRef<HTMLDivElement>(null);
  const lettersRef = useRef<HTMLSpanElement>(null);
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const versionRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (iconRef.current) {
        gsap.fromTo(
          iconRef.current,
          { opacity: 0, scale: 0.72, rotate: -8 },
          {
            opacity: 1,
            scale: 1,
            rotate: 0,
            duration: 0.52,
            delay: 0.1,
            ease: "back.out(1.6)",
            clearProps: "transform,opacity",
          },
        );
      }

      if (lettersRef.current) {
        const letters = lettersRef.current.querySelectorAll(".logo-letter");
        gsap.fromTo(
          letters,
          { opacity: 0, y: 10 },
          {
            opacity: 1,
            y: 0,
            duration: 0.36,
            stagger: 0.038,
            delay: 0.2,
            ease: "power3.out",
            clearProps: "transform,opacity",
          },
        );
      }

      if (subtitleRef.current && versionRef.current) {
        gsap.fromTo(
          subtitleRef.current,
          { opacity: 0, y: 8 },
          {
            opacity: 1,
            y: 0,
            duration: 0.38,
            delay: 0.46,
            ease: "power3.out",
            clearProps: "transform,opacity",
          },
        );
      }

      if (versionRef.current) {
        gsap.fromTo(
          versionRef.current,
          { opacity: 0, y: 8 },
          {
            opacity: 1,
            y: 0,
            duration: 0.38,

            delay: 0.46,
            ease: "power3.out",
            clearProps: "transform,opacity",
          },
        );
      }
    });
    return () => ctx.revert();
  }, []);

  return (
    <div
      className="flex w-full cursor-pointer items-center justify-start gap-1"
      onClick={() =>
        router.navigate({ to: "/", search: { section: undefined } })
      }
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          ref={iconRef}
          className="flex shrink-0 items-center justify-center"
        >
          <TempLogo width={43} height={28} />
        </div>
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span
            ref={lettersRef}
            className="text-heading shrink-0 text-lg leading-none font-bold tracking-[-0.025em]"
          >
            {LETTERS.map((letter, i) => (
              <span key={i} className="logo-letter inline-block">
                {letter}
              </span>
            ))}
          </span>
          {subtitle && (
            <span
              ref={subtitleRef}
              className="inline-block truncate text-lg leading-tight font-bold -tracking-wide text-neutral-950"
            >
              <span className="font-light">{subtitle}</span>
            </span>
          )}
        </div>
      </div>

      <p
        className="shrink-0 self-end pb-1 text-[10px] text-gray-500"
        ref={versionRef}
      >
        {import.meta.env.VITE_APP_VERSION}
      </p>
    </div>
  );
}
